import { useOrgData } from '@/org/contexts/OrgContext'
import { Box, Tag, Text, chakra } from '@chakra-ui/react'
import { EntityChange, EntityChangeType } from '@rolebase/shared/model/log'
import React, { Suspense, lazy, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Lazy loading because it's using the editor
const ObjectDiff = lazy(() => import('./ObjectDiff'))

const entityLabelKeys = {
  roles: 'LogEntityChanges.entity_roles',
  circles: 'LogEntityChanges.entity_circles',
  circlesMembers: 'LogEntityChanges.entity_circlesMembers',
  circlesLinks: 'LogEntityChanges.entity_circlesLinks',
} as const

const changeLabelKeys = {
  [EntityChangeType.Create]: 'LogEntityChanges.change_Create',
  [EntityChangeType.Update]: 'LogEntityChanges.change_Update',
  [EntityChangeType.Delete]: 'LogEntityChanges.change_Delete',
} as const

interface Props<Entity extends {}> {
  type: string
  entityChange: EntityChange<Entity>
}

export default function LogEntityChanges<Entity extends {}>({
  type,
  entityChange,
}: Props<Entity>) {
  const { t } = useTranslation()
  const orgData = useOrgData()

  // Name what the change is about, in the words the org chart uses. A bare
  // uuid says nothing, and this panel is read at a glance during a meeting to
  // decide — it is the only place showing what a proposal actually does.
  const entityName = useMemo(() => {
    const change = entityChange as any

    // The payload usually carries the name already (a role does). Reading it
    // first also survives an entity the org data no longer holds: a deletion,
    // or a change prepared against a circle archived since.
    const fromPayload =
      change.data?.name ?? change.newData?.name ?? change.prevData?.name
    if (typeof fromPayload === 'string' && fromPayload) return fromPayload

    if (!orgData) return undefined
    if (type === 'roles') return orgData.getRole(entityChange.id)?.name
    if (type === 'circles') {
      // A circle has no name of its own: it is an instance of a role.
      const roleId =
        orgData.getCircle(entityChange.id)?.roleId ??
        change.data?.roleId ??
        change.newData?.roleId
      return orgData.getRole(roleId)?.name
    }
    return undefined
  }, [type, entityChange, orgData])

  // Explicit tables rather than keys built by string concatenation: i18n typing
  // rejects those, and rightly so — a key assembled at runtime can go missing
  // without anything failing until a user reads it.
  const entityKey =
    entityLabelKeys[type as keyof typeof entityLabelKeys] ?? undefined
  const entityLabel = entityKey ? t(entityKey) : type
  const changeKey = changeLabelKeys[entityChange.type]

  return (
    <Suspense fallback={null}>
      <Box>
        <Text fontSize="sm" mb={2}>
          <Tag
            size="sm"
            colorScheme={
              entityChange.type === EntityChangeType.Create
                ? 'green'
                : entityChange.type === EntityChangeType.Update
                ? 'blue'
                : 'red'
            }
          >
            {changeKey ? t(changeKey) : entityChange.type}
          </Tag>{' '}
          {entityLabel}
          {entityName && (
            <>
              {' '}
              <chakra.strong>{entityName}</chakra.strong>
            </>
          )}
        </Text>

        {entityChange.type === EntityChangeType.Create && (
          <ObjectDiff value={entityChange.data} />
        )}

        {entityChange.type === EntityChangeType.Update && (
          <ObjectDiff
            value={entityChange.newData}
            compareValue={entityChange.prevData}
          />
        )}
      </Box>
    </Suspense>
  )
}
